import React from 'react';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  CreditCard,
  Hash,
  Calendar,
  User,
  CheckCircle,
  XCircle as XCircleIcon,
  FileText,
  Briefcase,
  Home,
  Clock,
} from 'lucide-react';

import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Customer } from '../../../services/customerService';

interface CustomerDetailViewProps {
  customer: Customer | null;
}

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Row: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="flex items-start gap-2 py-1.5 text-sm">
    {icon && <span className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</span>}
    <div className="min-w-0 flex-1 grid grid-cols-[100px_1fr] gap-2 sm:grid-cols-[110px_1fr]">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="text-gray-900 truncate">{value ?? '—'}</span>
    </div>
  </div>
);

const CustomerDetailView: React.FC<CustomerDetailViewProps> = ({ customer }) => {
  if (!customer) return null;

  const isActive = customer.status === 'active';
  const primaryContact = customer.contacts?.find((c) => c.isPrimary) || customer.contacts?.[0];
  const addressLine = [
    customer.address?.street,
    [customer.address?.city, customer.address?.state].filter(Boolean).join(', '),
    customer.address?.country,
    customer.address?.pincode,
  ]
    .filter(Boolean)
    .join(' · ') || '—';
  const billingLine =
    customer.billingAddress &&
    [
      customer.billingAddress.street,
      [customer.billingAddress.city, customer.billingAddress.state].filter(Boolean).join(', '),
      customer.billingAddress.country,
      customer.billingAddress.pincode,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <div className="flex flex-col gap-4">
      {/* Compact header */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <div className="w-10 h-10 rounded-lg bg-surface-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
          <Building2 size={20} className="text-gray-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {customer.companyName || customer.name}
          </h3>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-xs text-gray-500">Code: {customer.code || '—'}</span>
            {customer.customerType && (
              <Tag value={customer.customerType} severity="secondary" className="text-xs py-0" />
            )}
            <Tag
              value={isActive ? 'Active' : 'Inactive'}
              severity={isActive ? 'success' : 'danger'}
              icon={isActive ? <CheckCircle size={12} /> : <XCircleIcon size={12} />}
              className="text-xs py-0"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Company & contact */}
        <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
          <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Building2 size={16} className="text-gray-500" />
            <h4 className="text-sm font-semibold text-gray-800">Company & Contact</h4>
          </div>
          <div className="p-4 space-y-3">
            <Row label="Company" value={customer.companyName || customer.name} icon={<Building2 size={14} />} />
            <Row label="Email" value={customer.email} icon={<Mail size={14} />} />
            <Row label="Phone" value={customer.mobile || customer.phone} icon={<Phone size={14} />} />
            <Row label="Name" value={customer.name} icon={<User size={14} />} />
            {customer.website && (
              <Row
                label="Website"
                value={
                  <a
                    href={customer.website.startsWith('http') ? customer.website : `https://${customer.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate block"
                  >
                    {customer.website}
                  </a>
                }
                icon={<Globe size={14} />}
              />
            )}
            {customer.industry && (
              <Row label="Industry" value={customer.industry} icon={<Briefcase size={14} />} />
            )}
          </div>
        </div>

        {/* Address */}
        <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
          <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <MapPin size={16} className="text-gray-500" />
            <h4 className="text-sm font-semibold text-gray-800">Location</h4>
          </div>
          <div className="p-4 text-sm text-gray-700">
            <div className="mb-1 font-medium text-gray-900">Primary Address</div>
            <p className="leading-snug">{addressLine}</p>
            {billingLine && (
              <>
                <Divider className="!my-3" />
                <div className="mb-1 font-medium text-gray-900">Billing Address</div>
                <p className="leading-snug">{billingLine}</p>
              </>
            )}
          </div>
        </div>

        {/* Tax */}
        {(customer.gstNumber || customer.panNumber || customer.registrationNumber) && (
          <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
            <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <FileText size={16} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-800">Tax & Registration</h4>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4 text-sm">
              {customer.gstNumber && (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <CreditCard size={14} /> GST Number
                  </div>
                  <span className="text-gray-900 font-medium px-1">{customer.gstNumber}</span>
                </div>
              )}
              {customer.panNumber && (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <CreditCard size={14} /> PAN Number
                  </div>
                  <span className="text-gray-900 font-medium px-1">{customer.panNumber}</span>
                </div>
              )}
              {customer.registrationNumber && (
                <div className="flex flex-col gap-1 col-span-2">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <Hash size={14} /> Registration Number
                  </div>
                  <span className="text-gray-900 font-medium px-1">{customer.registrationNumber}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes & tags */}
        {(customer.notes || (customer.tags && customer.tags.length > 0)) && (
          <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
            <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <FileText size={16} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-800">Notes & Tags</h4>
            </div>
            <div className="p-4">
              {customer.notes && (
                <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{customer.notes}</p>
              )}
              {customer.tags && customer.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {customer.tags.map((tag, i) => (
                    <Tag key={i} value={tag} severity="info" className="text-xs py-0.5 px-2 rounded-full" />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact persons */}
        {customer.contacts && customer.contacts.length > 0 && (
          <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
            <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <User size={16} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-800">Contact Persons</h4>
            </div>
            <div className="p-4 space-y-3">
              {customer.contacts.map((contact, index) => (
                <div
                  key={index}
                  className="flex flex-col py-2 px-3 rounded-lg border border-gray-100 bg-gray-50"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{contact.name}</span>
                    {contact.isPrimary && (
                      <Tag value="Primary" severity="success" className="text-[10px] py-0 px-1.5 rounded-full" />
                    )}
                  </div>
                  {contact.designation && (
                    <span className="text-xs text-gray-500 mb-2">{contact.designation}</span>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                    {contact.email && (
                      <div className="flex items-center gap-1.5">
                        <Mail size={12} className="text-gray-400" /> {contact.email}
                      </div>
                    )}
                    {(contact.mobile || contact.phone) && (
                      <div className="flex items-center gap-1.5">
                        <Phone size={12} className="text-gray-400" /> {contact.mobile || contact.phone}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* System Information */}
        <div className="border border-gray-200 rounded-xl bg-gray-50/50 overflow-hidden">
          <div className="bg-gray-100/50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Clock size={16} className="text-gray-500" />
            <h4 className="text-sm font-semibold text-gray-800">System Information</h4>
          </div>
          <div className="p-4 grid grid-cols-1 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-2 bg-blue-100/50 text-blue-600 rounded-lg">
                <Calendar size={16} />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-gray-500 font-medium">Created On</span>
                <span className="text-gray-900 font-medium">{formatDate(customer.createdAt)}</span>
                {customer.createdBy && (
                  <span className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <User size={10} /> 
                    {typeof customer.createdBy === 'object' ? customer.createdBy.name || customer.createdBy.email : 'System'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailView;
